import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { useAusencias, TIPO_AUSENCIA_LABEL, TipoAusencia } from "@/contexts/AusenciasContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSpreadsheet } from "lucide-react";
import { format } from "date-fns";

interface Tecnico { user_id: string; nombre: string; email: string; rol: string }

const TIPOS: TipoAusencia[] = ["vacaciones", "baja_medica", "asuntos_propios", "formacion"];

const DAY = 86400000;
const toDate = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00`);
const round1 = (n: number) => Math.round(n * 10) / 10;

export const ResumenAusencias: React.FC<{ tecnicos: Tecnico[] }> = ({ tecnicos }) => {
  const { ausencias, cupos, modoComputo } = useAusencias();
  const modoLabel = modoComputo === "naturales" ? "días naturales" : "días laborables";

  const hoy = new Date();
  const [anio, setAnio] = useState<string>(String(hoy.getFullYear()));
  const [usarRango, setUsarRango] = useState(false);
  const [desde, setDesde] = useState(`${hoy.getFullYear()}-01-01`);
  const [hasta, setHasta] = useState(`${hoy.getFullYear()}-12-31`);
  const [tecnicoSel, setTecnicoSel] = useState<string>("all");
  const [incluirPendientes, setIncluirPendientes] = useState(false);

  const anios = useMemo(() => {
    const base = hoy.getFullYear();
    return Array.from({ length: 6 }, (_, i) => String(base + 1 - i));
  }, [hoy]);

  const rango = useMemo(() => {
    if (usarRango) return { ini: toDate(desde), fin: toDate(hasta) };
    return { ini: new Date(Number(anio), 0, 1), fin: new Date(Number(anio), 11, 31) };
  }, [usarRango, desde, hasta, anio]);

  const filtradas = useMemo(() => {
    return ausencias
      .filter(a => a.estado_validacion === "aprobada" || (incluirPendientes && a.estado_validacion === "pendiente"))
      .filter(a => tecnicoSel === "all" || a.tecnico_id === tecnicoSel)
      .map(a => {
        const ini = toDate(a.fecha_inicio);
        const fin = toDate(a.fecha_fin);
        const solapIni = new Date(Math.max(ini.getTime(), rango.ini.getTime()));
        const solapFin = new Date(Math.min(fin.getTime(), rango.fin.getTime()));
        if (solapFin.getTime() < solapIni.getTime()) return null;
        const totalDiasNat = Math.round((fin.getTime() - ini.getTime()) / DAY) + 1;
        const solapDiasNat = Math.round((solapFin.getTime() - solapIni.getTime()) / DAY) + 1;
        const dias = Number(a.dias_solicitados) * (solapDiasNat / totalDiasNat);
        return { ausencia: a, dias: round1(dias) };
      })
      .filter((x): x is { ausencia: typeof ausencias[number]; dias: number } => x !== null);
  }, [ausencias, incluirPendientes, tecnicoSel, rango]);

  const filas = useMemo(() => {
    const map = new Map<string, {
      tecnicoId: string;
      nombre: string;
      porTipo: Record<TipoAusencia, number>;
      total: number;
      count: number;
    }>();
    for (const { ausencia: a, dias } of filtradas) {
      let row = map.get(a.tecnico_id);
      if (!row) {
        row = {
          tecnicoId: a.tecnico_id,
          nombre: a.tecnico?.nombre || tecnicos.find(t => t.user_id === a.tecnico_id)?.nombre || a.tecnico_id.slice(0, 8),
          porTipo: { vacaciones: 0, baja_medica: 0, asuntos_propios: 0, formacion: 0 },
          total: 0,
          count: 0,
        };
        map.set(a.tecnico_id, row);
      }
      row.porTipo[a.tipo] += dias;
      row.total += dias;
      row.count += 1;
    }
    const anioCupo = usarRango ? toDate(desde).getFullYear() : Number(anio);
    const defaultDias = modoComputo === "naturales" ? 30 : 22;
    return Array.from(map.values())
      .map(r => {
        const cupo = cupos.find(c => c.tecnico_id === r.tecnicoId && c.anio === anioCupo);
        const totales = Number(cupo?.dias_totales ?? defaultDias);
        return {
          ...r,
          porTipo: TIPOS.reduce((acc, t) => ({ ...acc, [t]: round1(r.porTipo[t]) }), {} as Record<TipoAusencia, number>),
          total: round1(r.total),
          cupoTotal: totales,
          cupoRestante: round1(totales - r.porTipo.vacaciones),
        };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [filtradas, tecnicos, cupos, anio, usarRango, desde, modoComputo]);

  const totales = useMemo(() => {
    const base: Record<TipoAusencia, number> = { vacaciones: 0, baja_medica: 0, asuntos_propios: 0, formacion: 0 };
    let total = 0;
    let count = 0;
    for (const r of filas) {
      TIPOS.forEach(t => { base[t] += r.porTipo[t]; });
      total += r.total;
      count += r.count;
    }
    return {
      porTipo: TIPOS.reduce((acc, t) => ({ ...acc, [t]: round1(base[t]) }), {} as Record<TipoAusencia, number>),
      total: round1(total),
      count,
    };
  }, [filas]);

  const periodoLabel = usarRango
    ? `${format(rango.ini, "dd/MM/yyyy")} – ${format(rango.fin, "dd/MM/yyyy")}`
    : anio;

  const exportar = () => {
    const resumen = filas.map(r => ({
      Trabajador: r.nombre,
      Vacaciones: r.porTipo.vacaciones,
      "Baja médica": r.porTipo.baja_medica,
      "Asuntos propios": r.porTipo.asuntos_propios,
      Formación: r.porTipo.formacion,
      "Total días": r.total,
      "Nº ausencias": r.count,
      "Cupo vacaciones": r.cupoTotal,
      "Vacaciones consumidas": r.porTipo.vacaciones,
      "Vacaciones restantes": r.cupoRestante,
    }));
    resumen.push({
      Trabajador: "TOTAL",
      Vacaciones: totales.porTipo.vacaciones,
      "Baja médica": totales.porTipo.baja_medica,
      "Asuntos propios": totales.porTipo.asuntos_propios,
      Formación: totales.porTipo.formacion,
      "Total días": totales.total,
      "Nº ausencias": totales.count,
      "Cupo vacaciones": "" as unknown as number,
      "Vacaciones consumidas": "" as unknown as number,
      "Vacaciones restantes": "" as unknown as number,
    });

    const detalle = filtradas
      .slice()
      .sort((a, b) => a.ausencia.fecha_inicio.localeCompare(b.ausencia.fecha_inicio))
      .map(({ ausencia: a, dias }) => ({
        Trabajador: a.tecnico?.nombre || a.tecnico_id.slice(0, 8),
        Tipo: TIPO_AUSENCIA_LABEL[a.tipo],
        "Fecha inicio": format(toDate(a.fecha_inicio), "dd/MM/yyyy"),
        "Fecha fin": format(toDate(a.fecha_fin), "dd/MM/yyyy"),
        "Días en el periodo": dias,
        "Días solicitados": Number(a.dias_solicitados),
        Estado: a.estado_validacion,
        Motivo: a.motivo || "",
        "Motivo rechazo": a.motivo_rechazo || "",
        Solicitada: a.created_at ? format(new Date(a.created_at), "dd/MM/yyyy") : "",
        Revisada: a.fecha_revision ? format(new Date(a.fecha_revision), "dd/MM/yyyy") : "",
      }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumen), "Resumen");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detalle), "Detalle");
    const sufijo = usarRango ? `${desde}_${hasta}` : anio;
    XLSX.writeFile(wb, `ausencias-${sufijo}.xlsx`);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Trabajador</Label>
            <Select value={tecnicoSel} onValueChange={setTecnicoSel}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {tecnicos.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!usarRango ? (
            <div className="space-y-1">
              <Label className="text-xs">Año</Label>
              <Select value={anio} onValueChange={setAnio}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {anios.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
              </div>
            </>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Switch id="rango" checked={usarRango} onCheckedChange={setUsarRango} />
              <Label htmlFor="rango" className="text-xs">Usar rango de fechas</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="pend" checked={incluirPendientes} onCheckedChange={setIncluirPendientes} />
              <Label htmlFor="pend" className="text-xs">Incluir pendientes</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Periodo: {periodoLabel}</span>
          <Badge variant="secondary" className="text-xs">Cómputo en {modoLabel}</Badge>
        </div>
        <Button onClick={exportar} disabled={filas.length === 0} className="gap-2">
          <FileSpreadsheet className="h-4 w-4" /> Exportar a Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {TIPOS.map(t => (
          <Card key={t}>
            <CardHeader className="pb-1"><CardTitle className="text-xs font-medium text-muted-foreground">{TIPO_AUSENCIA_LABEL[t]}</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{totales.porTipo[t]}</p></CardContent>
          </Card>
        ))}
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-xs font-medium text-muted-foreground">Total días</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{totales.total}</p></CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {filas.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No hay ausencias en este periodo</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Trabajador</TableHead>
                  {TIPOS.map(t => <TableHead key={t} className="text-right">{TIPO_AUSENCIA_LABEL[t]}</TableHead>)}
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Nº</TableHead>
                  <TableHead className="text-right">Cupo vac.</TableHead>
                  <TableHead className="text-right">Vac. restantes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map(r => (
                  <TableRow key={r.tecnicoId}>
                    <TableCell className="font-medium">{r.nombre}</TableCell>
                    {TIPOS.map(t => <TableCell key={t} className="text-right">{r.porTipo[t] || "—"}</TableCell>)}
                    <TableCell className="text-right font-semibold">{r.total}</TableCell>
                    <TableCell className="text-right">{r.count}</TableCell>
                    <TableCell className="text-right">{r.cupoTotal}</TableCell>
                    <TableCell className="text-right">{r.cupoRestante}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-muted/50">
                  <TableCell className="font-semibold">TOTAL</TableCell>
                  {TIPOS.map(t => <TableCell key={t} className="text-right font-semibold">{totales.porTipo[t]}</TableCell>)}
                  <TableCell className="text-right font-semibold">{totales.total}</TableCell>
                  <TableCell className="text-right font-semibold">{totales.count}</TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
